<template>
  <div class="login-container">
    <el-card class="login-card">
      <div slot="header" class="login-header">
        <h2>教师登录</h2>
      </div>
      
      <el-form :model="loginForm" :rules="loginRules" ref="loginForm" label-width="80px" class="login-form">
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="loginForm.phone" placeholder="请输入手机号" @blur="onPhoneBlur"></el-input>
        </el-form-item>
        
        <el-form-item label="密码" prop="password">
          <el-input v-model="loginForm.password" type="password" placeholder="请输入密码" show-password @keyup.enter.native="handleLogin"></el-input>
        </el-form-item>
        
        <!-- 智能验证码区域 -->
        <el-form-item v-if="showCaptcha" label="验证码" prop="captcha">
          <div class="captcha-container">
            <el-input 
              v-model="loginForm.captcha" 
              placeholder="请输入验证码" 
              style="flex: 1; margin-right: 10px;"
              @keyup.enter.native="handleLogin">
            </el-input>
            <img 
              :src="captchaImage" 
              @click="refreshCaptcha"
              class="captcha-image"
              alt="验证码">
          </div>

        </el-form-item>
        

        
        <el-form-item>
          <el-button type="primary" @click="handleLogin" :loading="loading" class="theme-independent-button" style="width: 100%;">登录</el-button>
        </el-form-item>
      </el-form>
      
      <div class="register-link">
        <span>没有账号？</span>
        <el-button type="text" @click="goToRegister">快速注册</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'TeacherLogin',
  mounted() {
    // 确保登录页面不受任何主题类的影响
    document.body.classList.remove('chinese-theme', 'math-theme', 'english-theme');
  },
  beforeDestroy() {
    // 组件销毁前不需要特别处理，因为可能其他页面需要主题
  },
  data() {
    return {
      loading: false,
      showCaptcha: false,
      captchaImage: '',
      captchaUuid: '',
      loginForm: {
        phone: '',
        password: '',
        captcha: ''
      },
      loginRules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        captcha: []
      },
      redirectUrl: null  // 记录来源页面的URL
    }
  },
  
  created() {
    // 从路由参数中获取来源页面URL
    this.redirectUrl = this.$route.query.redirect || null;
  },
  methods: {
    // 手机号失去焦点时检查是否需要验证码
    async onPhoneBlur() {
      if (this.loginForm.phone && this.loginForm.phone.length === 11) {
        await this.checkCaptchaRequirement(this.loginForm.phone);
      }
    },
    
    // 检查是否需要验证码
    async checkCaptchaRequirement(phone) {
      try {
        const response = await this.$http.get('/teachers/check-captcha-required', {
          params: { phone }
        });
        
        this.showCaptcha = response.data.captchaRequired;
        
        if (this.showCaptcha) {
          await this.refreshCaptcha();
          // 动态添加验证码验证规则
          this.$set(this.loginRules, 'captcha', [{ required: true, message: '请输入验证码', trigger: 'blur' }]);
        } else {
          this.$set(this.loginRules, 'captcha', []);
          // 清空验证码相关数据
          this.loginForm.captcha = '';
          this.captchaImage = '';
        }
      } catch (error) {
        console.error('检查验证码需求失败:', error);
      }
    },
    
    // 刷新验证码
    async refreshCaptcha() {
      this.captchaUuid = this.generateUUID();
      try {
        const response = await this.$http.get(`/teachers/captcha-image/${this.captchaUuid}`);
        this.captchaImage = response.data.captcha.image;
      } catch (error) {
        console.error('刷新验证码失败:', error);
        this.$message.error('验证码加载失败');
      }
    },
    
    // 生成UUID
    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },
    
    async handleLogin() {
      try {
        await this.$refs.loginForm.validate()
        this.loading = true
        
        // 生成会话ID
        const sessionId = this.generateSessionId();
        
        const requestData = {
          phone: this.loginForm.phone,
          password: this.loginForm.password,
          deviceType: 'pc', // PC端登录
          sessionId: sessionId
        };
        
        // 如果显示验证码，则添加验证码信息
        if (this.showCaptcha) {
          requestData.captchaUuid = this.captchaUuid;
          requestData.captchaCode = this.loginForm.captcha;
        }
        
        const response = await this.$http.post('/teachers/login', requestData)
        
        if (response.data.success) {
          // 登录成功，清空验证码相关状态
          this.showCaptcha = false;
          this.loginForm.captcha = '';
          
          // 保存JWT、会话ID和用户信息到本地存储
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('sessionId', response.data.sessionId); // 保存服务器返回的会话ID
          localStorage.setItem('deviceType', 'pc'); // 记录设备类型
          localStorage.setItem('teacherInfo', JSON.stringify(response.data.teacher))
          
          // 如果有来源页面，直接跳转到来源页面
          if (this.redirectUrl) {
            this.$router.push(this.redirectUrl);
          } else if (response.data.hasClasses) {
            // 获取教师绑定的班级信息（使用新的安全接口）
            const boundClassesResponse = await this.$http.get('/teachers/bound-classes');
            
            if (boundClassesResponse.data.success) {
              const boundClasses = boundClassesResponse.data.boundClasses;
              
              if (boundClasses.length === 1) {
                // 如果只有一个班级，直接跳转到该班级的主页
                const classInfo = boundClasses[0];
                this.$router.push(`/class/${classInfo.classId}?subjectId=${classInfo.subjectId}`);
              } else {
                // 如果有多个班级或没有班级，跳转到/desktop界面
                this.$router.push('/desktop');
              }
            } else {
              // 如果获取班级信息失败，跳转到/desktop界面
              this.$router.push('/desktop');
            }
          } else {
            // 如果没有班级，跳转到/desktop界面
            this.$router.push('/desktop');
          }
          
          this.$message.success(response.data.message)
        } else {
          // 登录失败处理
          this.$message.error(response.data.message);
          
          // 更新验证码状态
          if (response.data.captchaRequired) {
            this.showCaptcha = true;
            this.$set(this.loginRules, 'captcha', [{ required: true, message: '请输入验证码', trigger: 'blur' }]);
            await this.refreshCaptcha();
            this.loginForm.captcha = '';
          } else {
            this.showCaptcha = false;
            this.$set(this.loginRules, 'captcha', []);
            this.loginForm.captcha = '';
          }
        }
      } catch (error) {
        // 区分表单验证错误和其他错误
        // Element UI表单验证失败会抛出包含字段错误信息的对象
        if (error.constructor.name === 'Error' && error.message.includes('validate')) {
          // 表单验证错误已经在Element UI层面处理了，不需要额外提示
          return;
        }
        
        // 检查是否是对象形式的表单验证错误
        if (typeof error === 'object' && error !== null && (error.captcha || error.phone || error.password)) {
          // 这些错误已经在Element UI层面通过红色提示显示了，不需要额外处理
          return;
        }
        
        // 检查是否是验证码相关的错误
        if (error.response && error.response.data && error.response.data.message) {
          this.$message.error(error.response.data.message);
          // 如果后端提示需要验证码，显示验证码输入框
          if (error.response.data.captchaRequired) {
            this.showCaptcha = true;
            this.$set(this.loginRules, 'captcha', [{ required: true, message: '请输入验证码', trigger: 'blur' }]);
            await this.refreshCaptcha();
            this.loginForm.captcha = '';
          }
        } else {
          this.$message.error('登录失败，请重试');
        }
      } finally {
        this.loading = false
      }
    },
    
    // 生成随机会话ID
    generateSessionId() {
      return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },
    
    goToRegister() {
      this.$router.push('/signin')
    }
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #409EFF, #64b5f6);
  padding: 20px;
}

.login-card {
  width: 100%;
  max-width: 400px;
  border-radius: 15px;
  overflow: hidden;
}

.login-header {
  text-align: center;
  background: linear-gradient(90deg, #409EFF, #66b5f6);
  color: white;
  padding: 20px;
  margin: -20px -20px 20px -20px;
}

.login-form {
  padding: 20px;
}

.register-link {
  text-align: center;
  margin-top: 20px;
}

.register-link span {
  color: #909399;
}

.el-button--text {
  color: #409EFF;
}

/* 确保登录按钮始终是蓝色，不受主题影响 */
.theme-independent-button {
  background: linear-gradient(90deg, #409EFF, #64b5f6) !important;
  border: none !important;
  color: white !important;
}

.theme-independent-button:hover,
.theme-independent-button:focus {
  background: linear-gradient(90deg, #64b5f6, #80ceff) !important;
  border: none !important;
  color: white !important;
}

.theme-independent-button:active {
  background: linear-gradient(90deg, #1a7fd9, #409EFF) !important;
  border: none !important;
  color: white !important;
}

/* 验证码样式 */
.captcha-container {
  display: flex;
  align-items: center;
}

.captcha-image {
  height: 40px;
  cursor: pointer;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  transition: all 0.3s;
}

.captcha-image:hover {
  border-color: #409EFF;
  transform: scale(1.05);
}




</style>